import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
    ChevronLeft, ChevronRight, ChevronDown, Plus, X, Send, StickyNote, Trash2,
    CalendarDays, Clock, Image as ImageIcon, Film, Loader2, Zap, Repeat, AlertCircle,
    Eye, List, LayoutGrid, Sparkles,
} from "lucide-react";
import { GROWTHOS_BASE, authHeaders } from "../config/api";

/* ════════════════════════════════════════════════════════════════════
   CONFIG
   ════════════════════════════════════════════════════════════════════ */

// n8n webhook base — same host the AI Media Generator already uses.
const N8N_BASE = "https://isf.tail2992e2.ts.net:8443/webhook";
// TEMP: true = n8n editor test URL (/webhook-test/, works before Publish; click "Listen for test event"
// on the "Schedule - Receive JSON" node first). Set to false once the workflow is published.
const N8N_TEST_MODE = false;//true;
const SCHEDULE_URL = `${N8N_TEST_MODE ? N8N_BASE.replace(/\/webhook$/, "/webhook-test") : N8N_BASE}/social-media-schedule`;
const N8N_ORIGIN = (() => { try { return new URL(N8N_BASE).origin; } catch { return ""; } })();

// One n8n timer (Wait node) is created per post, so repeats are capped.
const MAX_OCCURRENCES = 120;
const LIBRARY_LIMIT = 60;
const TZ = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "UTC"; } })();

const PLATFORMS = [
    { id: "facebook", name: "Facebook", type: "image", outputKey: "facebook" },
    { id: "instagram", name: "Instagram", type: "image", outputKey: "instagram" },
    { id: "linkedin", name: "LinkedIn", type: "image", outputKey: "linkedin" },
    { id: "instagram_story", name: "Insta Story", type: "video", outputKey: "video" },
    { id: "youtube_shorts", name: "YT Shorts", type: "video", outputKey: "video" },
];
const PLATFORM_BY_ID = Object.fromEntries(PLATFORMS.map((p) => [p.id, p]));

const THEMED_SCROLLBAR =
    "[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 " +
    "[&::-webkit-scrollbar-track]:bg-transparent " +
    "[&::-webkit-scrollbar-thumb]:bg-orange-500/20 [&::-webkit-scrollbar-thumb]:rounded-full " +
    "[&::-webkit-scrollbar-thumb:hover]:bg-orange-500/40 " +
    "[scrollbar-width:thin] [scrollbar-color:rgba(249,115,22,0.25)_transparent]";

const INPUT =
    "w-full bg-white/5 border border-white/10 focus:border-orange-600/50 rounded-lg px-3 py-2.5 text-sm text-white " +
    "placeholder:text-gray-600 outline-none transition [color-scheme:dark]";

/* ════════════════════════════════════════════════════════════════════
   STORAGE ADAPTER
   Saved content + calendar entries live here. Today this is the browser's
   localStorage. When the real storage link is available, only these two
   functions need to change (e.g. fetch GET/PUT to that service).
   ════════════════════════════════════════════════════════════════════ */
const storage = {
    async get(key) {
        try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : null; } catch { return null; }
    },
    async set(key, value) {
        try { localStorage.setItem(key, JSON.stringify(value)); return true; } catch { return false; }
    },
};

const storageKeyPrefix = () => {
    try {
        const u = JSON.parse(localStorage.getItem("userData") || "null");
        return `isf_calendar:${u?.email || u?.id || "default"}`;
    } catch { return "isf_calendar:default"; }
};

/* ════════════════════════════════════════════════════════════════════
   DATE HELPERS
   ════════════════════════════════════════════════════════════════════ */
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const hm = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const parseYmd = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => addDays(d, -d.getDay());
const daysIn = (y, m) => new Date(y, m + 1, 0).getDate();
const addMonthsClamped = (d, n) => {
    const t = new Date(d.getFullYear(), d.getMonth() + n, 1);
    t.setDate(Math.min(d.getDate(), daysIn(t.getFullYear(), t.getMonth())));
    return t;
};
const fmtTime = (t) => {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    return `${h % 12 || 12}:${pad(m)} ${h >= 12 ? "PM" : "AM"}`;
};
const fmtLong = (d) => d.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short", year: "numeric" });
const fmtShort = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const PERIODS = [
    { id: "1d", label: "1 day" },
    { id: "2d", label: "2 days" },
    { id: "1w", label: "1 week" },
    { id: "1m", label: "1 month" },
    { id: "1y", label: "1 year" },
];
function periodEnd(startStr, id) {
    const s = parseYmd(startStr);
    let e;
    if (id === "1d") e = s;
    else if (id === "2d") e = addDays(s, 1);
    else if (id === "1w") e = addDays(s, 6);
    else if (id === "1m") e = addDays(addMonthsClamped(s, 1), -1);
    else e = addDays(addMonthsClamped(s, 12), -1);
    return ymd(e);
}

/** All the date-times a schedule should fire on. */
export function buildOccurrences({ date, time, repeat, until }) {
    const base = parseYmd(date);
    const [hh, mm] = time.split(":").map(Number);
    const at = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), hh, mm, 0, 0);
    const first = at(base);
    if (repeat === "none" || !until) return { list: [first], truncated: false };

    const last = at(parseYmd(until));
    const list = [];
    let truncated = false;
    for (let i = 0; ; i++) {
        let d;
        if (repeat === "daily") d = addDays(base, i);
        else if (repeat === "weekly") d = addDays(base, 7 * i);
        else if (repeat === "monthly") d = addMonthsClamped(base, i);
        else d = addMonthsClamped(base, 12 * i);
        const cur = at(d);
        if (cur > last) break;
        if (list.length >= MAX_OCCURRENCES) { truncated = true; break; }
        list.push(cur);
    }
    return { list, truncated };
}

const uid = (p) => `${p}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const parseHashtags = (s) => (s || "").split(/[\s,#]+/).map((h) => h.trim()).filter(Boolean);
const libThumb = (outputs) => outputs?.facebook?.url || outputs?.instagram?.url || outputs?.linkedin?.url || null;
const availablePlatforms = (content) => PLATFORMS.filter((p) => content?.outputs?.[p.outputKey]?.url);

/** Backend (Groq vision) — looks at the post image / video and suggests caption + hashtags. */
async function suggestCaptionAndHashtags(content, platformIds) {
    const seen = new Set();
    const media = [];
    (platformIds.length ? platformIds : availablePlatforms(content).map((p) => p.id)).forEach((id) => {
        const p = PLATFORM_BY_ID[id];
        const url = content?.outputs?.[p?.outputKey]?.url;
        if (url && !seen.has(url)) { seen.add(url); media.push({ url, type: p.type === "video" ? "video" : "image" }); }
    });
    if (!media.length) throw new Error("This content has no image or video to analyse.");
    const res = await fetch(`${GROWTHOS_BASE}/api/suggest-post-caption`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ media: media.slice(0, 3), platforms: platformIds }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) throw new Error(data.message || data.detail || "Could not generate a suggestion.");
    return data;
}

/* ════════════════════════════════════════════════════════════════════
   n8n CALLS
   ════════════════════════════════════════════════════════════════════ */
function buildPlatformsPayload(content, platformIds, caption, hashtags) {
    const out = {};
    platformIds.forEach((id) => {
        const p = PLATFORM_BY_ID[id];
        const media = content.outputs[p.outputKey];
        if (!media?.url) return;
        if (p.type === "image") {
            out[id] = { enabled: true, media_url: media.url, media_type: "image", caption, hashtags };
        } else if (id === "instagram_story") {
            out[id] = { enabled: true, media_url: media.url, media_type: "video" };
        } else {
            out[id] = { enabled: true, media_url: media.url, media_type: "video", caption, hashtags };
        }
    });
    return out;
}

async function sendSchedule(payload) {
    let res;
    try {
        res = await fetch(SCHEDULE_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
    } catch {
        throw new Error("request blocked or unreachable (CORS / network) — check the browser console");
    }
    if (!res.ok) {
        const body = (await res.text().catch(() => "")).slice(0, 160);
        throw new Error(`n8n responded ${res.status}${body ? ` — ${body}` : ""}`);
    }
    const data = await res.json().catch(() => ({}));
    let resumePath = null;
    try { const u = new URL(data.resume_url); resumePath = u.pathname + u.search; } catch { /* no resume url */ }
    return { resumePath };
}

/** Wakes the waiting n8n execution early: action = "cancel" | "now".
 *  no-cors + urlencoded body = a "simple" request (no preflight), so it works from the browser. */
async function fireResume(resumePath, action) {
    if (!resumePath) return false;
    try {
        await fetch(N8N_ORIGIN + resumePath, { method: "POST", mode: "no-cors", body: new URLSearchParams({ action }) });
        return true;
    } catch { return false; }
}

async function runPool(tasks, limit) {
    const results = new Array(tasks.length);
    let next = 0;
    const worker = async () => {
        while (next < tasks.length) {
            const i = next++;
            try { results[i] = { ok: true, value: await tasks[i]() }; }
            catch (e) { results[i] = { ok: false, error: e }; }
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
    return results;
}

/* ════════════════════════════════════════════════════════════════════
   STORE HOOK — call once in the page, pass the result to <CampaignCalendar />
   and `store.addToLibrary` to the AI Media Generator (onContentReady).
   ════════════════════════════════════════════════════════════════════ */
export function useContentCalendarStore() {
    const prefix = useMemo(storageKeyPrefix, []);
    const [library, setLibrary] = useState([]);
    const [entries, setEntries] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const entriesRef = useRef(entries);
    entriesRef.current = entries;

    useEffect(() => {
        let alive = true;
        (async () => {
            const [l, e] = await Promise.all([storage.get(`${prefix}:library`), storage.get(`${prefix}:entries`)]);
            if (!alive) return;
            // merge (not overwrite) in case something was added before the load finished
            setLibrary((prev) => [...prev, ...(Array.isArray(l) ? l : []).filter((x) => !prev.some((p) => p.id === x.id))]);
            setEntries((prev) => [...prev, ...(Array.isArray(e) ? e : []).filter((x) => !prev.some((p) => p.id === x.id))]);
            setLoaded(true);
        })();
        return () => { alive = false; };
    }, [prefix]);

    useEffect(() => { if (loaded) storage.set(`${prefix}:library`, library); }, [library, loaded, prefix]);
    useEffect(() => { if (loaded) storage.set(`${prefix}:entries`, entries); }, [entries, loaded, prefix]);

    // Called by the AI Media Generator whenever content is generated (images first, video later).
    const addToLibrary = useCallback(({ id, outputs }) => {
        if (!id || !outputs) return;
        const clean = {};
        Object.entries(outputs).forEach(([k, v]) => {
            if (v && v.url) clean[k] = { url: v.url, type: v.type, download_url: v.download_url || v.url };
        });
        if (!Object.keys(clean).length) return;
        setLibrary((prev) => {
            const i = prev.findIndex((x) => x.id === id);
            if (i >= 0) {
                const next = [...prev];
                next[i] = { ...next[i], outputs: { ...next[i].outputs, ...clean } };
                return next;
            }
            return [{ id, createdAt: new Date().toISOString(), outputs: clean }, ...prev].slice(0, LIBRARY_LIMIT);
        });
    }, []);

    const removeFromLibrary = useCallback((id) => setLibrary((p) => p.filter((x) => x.id !== id)), []);
    const addEntry = useCallback((entry) => setEntries((p) => [...p, entry]), []);

    const cancelOccurrence = useCallback(async (entryId, occId) => {
        const o = entriesRef.current.find((x) => x.id === entryId)?.occurrences?.find((x) => x.id === occId);
        if (o?.resumePath) await fireResume(o.resumePath, "cancel");
        setEntries((p) => p.map((e) => e.id !== entryId ? e : {
            ...e, occurrences: e.occurrences.map((x) => x.id === occId ? { ...x, status: "cancelled" } : x),
        }));
    }, []);

    const postNow = useCallback(async (entryId, occId) => {
        const o = entriesRef.current.find((x) => x.id === entryId)?.occurrences?.find((x) => x.id === occId);
        if (o?.resumePath) await fireResume(o.resumePath, "now");
        const nowIso = new Date().toISOString();
        setEntries((p) => p.map((e) => e.id !== entryId ? e : {
            ...e, occurrences: e.occurrences.map((x) => x.id === occId ? { ...x, status: "sent", at: nowIso } : x),
        }));
    }, []);

    const removeEntry = useCallback(async (id) => {
        const e = entriesRef.current.find((x) => x.id === id);
        if (e?.occurrences) {
            const pending = e.occurrences.filter((o) => o.status === "scheduled" && new Date(o.at) > new Date());
            await Promise.all(pending.map((o) => fireResume(o.resumePath, "cancel")));
        }
        setEntries((p) => p.filter((x) => x.id !== id));
    }, []);

    return { library, entries, loaded, addToLibrary, removeFromLibrary, addEntry, cancelOccurrence, postNow, removeEntry };
}

const occStatus = (occ, now) => {
    if (occ.status === "cancelled") return "cancelled";
    if (occ.status === "sent" || new Date(occ.at) <= now) return "sent";
    return "scheduled";
};
const STATUS_STYLE = {
    scheduled: "bg-orange-500/10 text-orange-400 border-orange-500/30",
    sent: "bg-green-500/10 text-green-400 border-green-500/30",
    cancelled: "bg-white/5 text-gray-500 border-white/10 line-through",
    note: "bg-sky-500/10 text-sky-300 border-sky-500/30",
};
const STATUS_LABEL = { scheduled: "Scheduled", sent: "Sent", cancelled: "Cancelled" };

/* ════════════════════════════════════════════════════════════════════
   SMALL UI PIECES
   ════════════════════════════════════════════════════════════════════ */
const Modal = ({ title, subtitle, onClose, children, footer }) => (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm px-3 sm:px-4" onClick={onClose}>
        <div
            className="bg-[#0a0a0a] border border-orange-600/30 rounded-2xl w-full max-w-xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="px-5 sm:px-7 pt-5 sm:pt-6 pb-4 border-b border-white/5 flex items-start justify-between gap-4">
                <div>
                    <h3 className="text-lg font-bold text-white">{title}</h3>
                    {subtitle && <p className="text-gray-400 text-xs mt-1">{subtitle}</p>}
                </div>
                <button onClick={onClose} className="text-gray-500 hover:text-white transition" aria-label="Close"><X size={20} /></button>
            </div>
            <div className={`px-5 sm:px-7 py-5 overflow-y-auto space-y-5 ${THEMED_SCROLLBAR}`}>{children}</div>
            {footer && <div className="px-5 sm:px-7 py-4 border-t border-white/5 flex flex-wrap items-center justify-end gap-3">{footer}</div>}
        </div>
    </div>
);

const Label = ({ children }) => <label className="block text-xs font-semibold text-gray-400 mb-1.5">{children}</label>;

const Thumb = ({ outputs, size = 44 }) => {
    const url = libThumb(outputs);
    return (
        <div className="shrink-0 rounded-lg overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center" style={{ width: size, height: size }}>
            {url
                ? <img src={url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                : <Film size={size / 2.4} className="text-gray-500" />}
        </div>
    );
};

const OutputBadges = ({ outputs }) => (
    <div className="flex flex-wrap gap-1">
        {["facebook", "instagram", "linkedin"].filter((k) => outputs?.[k]?.url).map((k) => (
            <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-gray-400 border border-white/10 capitalize">{k === "linkedin" ? "LinkedIn" : k}</span>
        ))}
        {outputs?.video?.url && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-300 border border-red-500/20">Video</span>}
    </div>
);

const PeriodChips = ({ start, value, onPick }) => (
    <div className="flex flex-wrap gap-2">
        {PERIODS.map((p) => (
            <button
                type="button" key={p.id} onClick={() => onPick(p.id, periodEnd(start, p.id))}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${value === p.id ? "bg-orange-600 border-orange-600 text-white" : "border-white/10 text-gray-300 hover:border-orange-500/50 hover:text-orange-400"}`}
            >
                {p.label}
            </button>
        ))}
    </div>
);

/* ════════════════════════════════════════════════════════════════════
   SCHEDULE POST MODAL
   ════════════════════════════════════════════════════════════════════ */
const ScheduleModal = ({ store, initialDate, initialContentId, onClose, onDone }) => {
    const { library, addEntry } = store;
    const todayStr = ymd(new Date());
    const [contentId, setContentId] = useState(initialContentId || "");
    const [platforms, setPlatforms] = useState({});
    const [caption, setCaption] = useState("");
    const [hashtags, setHashtags] = useState("");
    const [date, setDate] = useState(initialDate || todayStr);
    const [time, setTime] = useState(() => {
        const t = new Date(Date.now() + 3600 * 1000);
        return ymd(t) === (initialDate || todayStr) ? `${pad(t.getHours())}:00` : "09:00";
    });
    const [repeat, setRepeat] = useState("none");
    const [period, setPeriod] = useState("");
    const [until, setUntil] = useState("");
    const [saving, setSaving] = useState(false);
    const [progress, setProgress] = useState("");
    const [error, setError] = useState("");
    const [suggesting, setSuggesting] = useState(false);

    const content = library.find((x) => x.id === contentId);
    const available = availablePlatforms(content);

    const pickContent = (id) => {
        setContentId(id);
        const c = library.find((x) => x.id === id);
        setPlatforms(Object.fromEntries(availablePlatforms(c).map((p) => [p.id, true])));
        setError("");
    };
    useEffect(() => { if (initialContentId) pickContent(initialContentId); /* eslint-disable-next-line */ }, []);

    const preview = useMemo(() => {
        if (!date || !time) return null;
        if (repeat !== "none" && (!until || until < date)) return null;
        return buildOccurrences({ date, time, repeat, until });
    }, [date, time, repeat, until]);

    const suggest = async () => {
        if (!content) return setError("Pick the saved content first, then click Suggest.");
        setError("");
        setSuggesting(true);
        try {
            const chosenIds = PLATFORMS.filter((p) => platforms[p.id] && content?.outputs?.[p.outputKey]?.url).map((p) => p.id);
            const r = await suggestCaptionAndHashtags(content, chosenIds);
            if (r.caption) setCaption(r.caption);
            if (r.hashtags) setHashtags(r.hashtags);
        } catch (e) {
            setError(e.message || "Could not generate a suggestion.");
        } finally {
            setSuggesting(false);
        }
    };

    const submit = async () => {
        setError("");
        const chosen = PLATFORMS.filter((p) => platforms[p.id] && content?.outputs?.[p.outputKey]?.url).map((p) => p.id);
        if (!content) return setError("Pick the saved content you want to post.");
        if (!chosen.length) return setError("Select at least one platform.");
        if (!date || !time) return setError("Choose a date and time.");
        if (repeat !== "none" && (!until || until < date)) return setError("Choose a valid end date for the repeat.");
        const { list, truncated } = buildOccurrences({ date, time, repeat, until });
        if (!list.length) return setError("No posting dates in that range.");
        if (list[0].getTime() < Date.now() - 60 * 1000) return setError("The first post time is in the past.");

        const entryId = uid("sch");
        const tags = parseHashtags(hashtags);
        const platformsPayload = buildPlatformsPayload(content, chosen, caption, tags);

        setSaving(true);
        let done = 0;
        setProgress(`Scheduling 0/${list.length}…`);
        const tasks = list.map((when, i) => async () => {
            const occId = `${entryId}_${i + 1}`;
            const { resumePath } = await sendSchedule({
                schedule_id: occId,
                parent_id: entryId,
                scheduled_at: when.toISOString(),
                timezone: TZ,
                platforms: platformsPayload,
            });
            setProgress(`Scheduling ${++done}/${list.length}…`);
            return { id: occId, at: when.toISOString(), status: "scheduled", resumePath };
        });
        const results = await runPool(tasks, 4);
        const occurrences = results.filter((r) => r.ok).map((r) => r.value);
        const failed = results.length - occurrences.length;
        setSaving(false);
        setProgress("");

        if (!occurrences.length) {
            const why = results.find((r) => !r.ok)?.error?.message;
            return setError(`Could not reach the n8n scheduler${why ? `: ${why}` : ""}. Check that the workflow is published and active, then try again.`);
        }
        addEntry({
            id: entryId, kind: "schedule", date, time, repeat, until: repeat === "none" ? "" : until,
            contentId, outputs: Object.fromEntries(Object.entries(content.outputs).map(([k, v]) => [k, { url: v.url, type: v.type }])),
            platforms: chosen, caption, hashtags, occurrences, createdAt: new Date().toISOString(),
        });
        const notes = [];
        if (failed) notes.push(`${failed} of ${list.length} could not be scheduled.`);
        if (truncated) notes.push(`Limited to the first ${MAX_OCCURRENCES} posts.`);
        onDone(new Date(occurrences[0].at), notes.join(" "));
    };

    return (
        <Modal
            title="Schedule a post"
            subtitle="Pick saved content, a date and time. n8n will publish it automatically at that time."
            onClose={saving ? () => { } : onClose}
            footer={
                <>
                    {error && <span className="mr-auto text-xs text-red-400 flex items-center gap-1.5"><AlertCircle size={14} />{error}</span>}
                    <button onClick={onClose} disabled={saving} className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-300 border border-white/10 hover:border-white/30 transition disabled:opacity-50">Cancel</button>
                    <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-orange-600 hover:bg-orange-500 text-white transition disabled:opacity-60">
                        {saving ? <><Loader2 size={15} className="animate-spin" />{progress}</> : <><Send size={15} />Schedule</>}
                    </button>
                </>
            }
        >
            <div>
                <Label>Content</Label>
                {library.length === 0 ? (
                    <p className="text-xs text-gray-500 bg-white/5 border border-white/10 rounded-lg px-3 py-3">
                        No saved content yet. Generate something in the AI Media Generator above — it is saved here automatically.
                    </p>
                ) : (
                    <div className={`grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-40 overflow-y-auto pr-1 ${THEMED_SCROLLBAR}`}>
                        {library.map((c) => (
                            <button
                                type="button" key={c.id} onClick={() => pickContent(c.id)} title={new Date(c.createdAt).toLocaleString()}
                                className={`rounded-lg p-1 border transition ${contentId === c.id ? "border-orange-500 bg-orange-500/10" : "border-white/10 hover:border-white/30"}`}
                            >
                                <div className="aspect-square w-full rounded-md overflow-hidden bg-white/5 flex items-center justify-center">
                                    {libThumb(c.outputs)
                                        ? <img src={libThumb(c.outputs)} alt="" className="w-full h-full object-cover" />
                                        : <Film size={18} className="text-gray-500" />}
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {content && (
                <div>
                    <Label>Post on</Label>
                    <div className="flex flex-wrap gap-2">
                        {PLATFORMS.map((p) => {
                            const ok = available.some((a) => a.id === p.id);
                            const on = !!platforms[p.id] && ok;
                            return (
                                <button
                                    type="button" key={p.id} disabled={!ok}
                                    onClick={() => setPlatforms((s) => ({ ...s, [p.id]: !s[p.id] }))}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${on ? "bg-orange-600 border-orange-600 text-white" : "border-white/10 text-gray-300 hover:border-orange-500/50"} ${!ok ? "opacity-30 cursor-not-allowed" : ""}`}
                                >
                                    {p.name}{!ok && " · no content"}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">Let AI look at your image/video and suggest a caption and hashtags.</p>
                <button
                    type="button" onClick={suggest} disabled={suggesting || !content}
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-orange-500/40 text-orange-400 hover:bg-orange-500/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {suggesting ? <><Loader2 size={13} className="animate-spin" />Analysing…</> : <><Sparkles size={13} />Suggest</>}
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <Label>Caption</Label>
                    <textarea rows={3} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Write the post caption…" className={`${INPUT} resize-none`} />
                </div>
                <div>
                    <Label>Hashtags</Label>
                    <textarea rows={3} value={hashtags} onChange={(e) => setHashtags(e.target.value)} placeholder="#marketing #ai" className={`${INPUT} resize-none`} />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label>Date</Label>
                    <input type="date" min={todayStr} value={date} onChange={(e) => { setDate(e.target.value); setPeriod(""); setUntil(""); }} className={INPUT} />
                </div>
                <div>
                    <Label>Time</Label>
                    <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={INPUT} />
                </div>
            </div>

            <div>
                <Label>Repeat</Label>
                <select value={repeat} onChange={(e) => { setRepeat(e.target.value); setPeriod(""); setUntil(""); }} className={INPUT}>
                    <option value="none" className="bg-[#0a0a0a]">Does not repeat</option>
                    <option value="daily" className="bg-[#0a0a0a]">Every day</option>
                    <option value="weekly" className="bg-[#0a0a0a]">Every week</option>
                    <option value="monthly" className="bg-[#0a0a0a]">Every month</option>
                    <option value="yearly" className="bg-[#0a0a0a]">Every year</option>
                </select>
            </div>

            {repeat !== "none" && (
                <div className="space-y-3">
                    <div>
                        <Label>Keep posting for</Label>
                        <PeriodChips start={date} value={period} onPick={(id, end) => { setPeriod(id); setUntil(end); }} />
                    </div>
                    <div>
                        <Label>…or until</Label>
                        <input type="date" min={date} value={until} onChange={(e) => { setUntil(e.target.value); setPeriod(""); }} className={INPUT} />
                    </div>
                </div>
            )}

            {preview && (
                <div className="text-xs text-gray-400 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 flex items-start gap-2">
                    <Clock size={14} className="mt-0.5 shrink-0 text-orange-400" />
                    <span>
                        {preview.list.length === 1
                            ? <>Will post once on <b className="text-white">{fmtLong(preview.list[0])}</b> at <b className="text-white">{fmtTime(hm(preview.list[0]))}</b>.</>
                            : <>Will post <b className="text-white">{preview.list.length} times</b>, from {fmtShort(preview.list[0])} to {fmtShort(preview.list[preview.list.length - 1])}, at <b className="text-white">{fmtTime(time)}</b>.</>}
                        {preview.truncated && <span className="text-orange-400"> Limited to {MAX_OCCURRENCES} posts per schedule.</span>}
                    </span>
                </div>
            )}
        </Modal>
    );
};

/* ════════════════════════════════════════════════════════════════════
   NOTE MODAL
   ════════════════════════════════════════════════════════════════════ */
const NoteModal = ({ store, initialDate, onClose, onDone }) => {
    const todayStr = ymd(new Date());
    const [date, setDate] = useState(initialDate || todayStr);
    const [time, setTime] = useState("");
    const [period, setPeriod] = useState("");
    const [endDate, setEndDate] = useState("");
    const [text, setText] = useState("");
    const [error, setError] = useState("");

    const submit = () => {
        if (!text.trim()) return setError("Write something for the note.");
        if (endDate && endDate < date) return setError("End date must be on or after the start date.");
        store.addEntry({
            id: uid("note"), kind: "note", date, endDate: endDate && endDate > date ? endDate : "",
            time, text: text.trim(), createdAt: new Date().toISOString(),
        });
        onDone(parseYmd(date), "");
    };

    return (
        <Modal
            title="Add a note"
            subtitle="A reminder on the calendar — notes are not posted anywhere."
            onClose={onClose}
            footer={
                <>
                    {error && <span className="mr-auto text-xs text-red-400 flex items-center gap-1.5"><AlertCircle size={14} />{error}</span>}
                    <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-300 border border-white/10 hover:border-white/30 transition">Cancel</button>
                    <button onClick={submit} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-orange-600 hover:bg-orange-500 text-white transition"><StickyNote size={15} />Save note</button>
                </>
            }
        >
            <div>
                <Label>Note</Label>
                <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Diwali offer starts, collect testimonial videos…" className={`${INPUT} resize-none`} />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label>Date</Label>
                    <input type="date" value={date} onChange={(e) => { setDate(e.target.value); setPeriod(""); setEndDate(""); }} className={INPUT} />
                </div>
                <div>
                    <Label>Time (optional)</Label>
                    <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={INPUT} />
                </div>
            </div>
            <div>
                <Label>Spans</Label>
                <PeriodChips start={date} value={period} onPick={(id, end) => { setPeriod(id); setEndDate(end); }} />
            </div>
            <div>
                <Label>…or until</Label>
                <input type="date" min={date} value={endDate} onChange={(e) => { setEndDate(e.target.value); setPeriod(""); }} className={INPUT} />
            </div>
        </Modal>
    );
};

/* ════════════════════════════════════════════════════════════════════
   CALENDAR VIEWS  (view-only — no scheduling logic in here)
   ════════════════════════════════════════════════════════════════════ */
const chipClass = (it, now) =>
    it.kind === "note" ? STATUS_STYLE.note : STATUS_STYLE[occStatus(it.occ, now)];

const itemLabel = (it) => {
    if (it.kind === "note") return it.entry.text;
    const names = it.entry.platforms.map((id) => PLATFORM_BY_ID[id]?.name).filter(Boolean);
    return `${names[0] || "Post"}${names.length > 1 ? ` +${names.length - 1}` : ""}`;
};

const MonthView = ({ cursor, selected, items, today, now, onSelect }) => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = startOfWeek(first);
    const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i));
    return (
        <div>
            <div className="grid grid-cols-7 gap-1.5 mb-1.5">
                {WEEKDAYS.map((w) => (
                    <div key={w} className="py-1 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-500">{w}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-1.5">
                {cells.map((d) => {
                    const k = ymd(d);
                    const list = items.get(k) || [];
                    const inMonth = d.getMonth() === cursor.getMonth();
                    return (
                        <button
                            key={k} onClick={() => onSelect(k)}
                            className={`relative min-h-[52px] sm:min-h-[92px] p-1.5 sm:p-2 text-left rounded-xl border flex flex-col gap-1 transition ${selected === k ? "border-orange-500 bg-orange-500/10" : "border-white/5 bg-white/[0.02] hover:bg-white/5 hover:border-white/15"} ${inMonth ? "" : "opacity-35"}`}
                        >
                            <span className={`text-xs font-semibold inline-flex w-6 h-6 items-center justify-center rounded-full ${k === today ? "bg-orange-600 text-white" : "text-gray-300"}`}>{d.getDate()}</span>
                            {list.length > 0 && (
                                <span className="hidden sm:inline-flex absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 items-center justify-center rounded-full bg-orange-500/20 text-orange-300 text-[10px] font-bold">{list.length}</span>
                            )}
                            <div className="hidden sm:flex flex-col gap-1 w-full">
                                {list.slice(0, 2).map((it) => (
                                    <span key={it.key} className={`text-[10px] truncate rounded px-1.5 py-0.5 border ${chipClass(it, now)}`}>
                                        {it.time ? `${fmtTime(it.time)} ` : ""}{itemLabel(it)}
                                    </span>
                                ))}
                                {list.length > 2 && <span className="text-[10px] text-gray-500">+{list.length - 2} more</span>}
                            </div>
                            <div className="flex sm:hidden gap-0.5 flex-wrap">
                                {list.slice(0, 4).map((it) => (
                                    <span key={it.key} className={`w-1.5 h-1.5 rounded-full ${it.kind === "note" ? "bg-sky-400" : "bg-orange-500"}`} />
                                ))}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const WeekView = ({ cursor, selected, items, today, now, onSelect }) => {
    const start = startOfWeek(cursor);
    return (
        <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
            {Array.from({ length: 7 }, (_, i) => addDays(start, i)).map((d) => {
                const k = ymd(d);
                const list = items.get(k) || [];
                return (
                    <div key={k} className={`rounded-xl border p-2 min-h-[72px] sm:min-h-[180px] ${selected === k ? "border-orange-500/60 bg-orange-500/5" : "border-white/10 bg-white/[0.02]"}`}>
                        <button onClick={() => onSelect(k)} className="w-full flex sm:block items-center gap-2 text-left mb-2">
                            <span className="text-[11px] uppercase tracking-wide text-gray-500">{WEEKDAYS[d.getDay()]}</span>
                            <span className={`ml-1 sm:ml-0 sm:block text-sm font-semibold ${k === today ? "text-orange-400" : "text-white"}`}>{d.getDate()} {MONTHS[d.getMonth()]}</span>
                        </button>
                        <div className="flex flex-col gap-1">
                            {list.map((it) => (
                                <span key={it.key} className={`text-[11px] truncate rounded px-1.5 py-1 border ${chipClass(it, now)}`}>
                                    {it.time ? `${fmtTime(it.time)} · ` : ""}{itemLabel(it)}
                                </span>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const YearView = ({ cursor, items, today, onPickMonth, onPickDay }) => {
    const year = cursor.getFullYear();
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {Array.from({ length: 12 }, (_, m) => {
                const first = new Date(year, m, 1);
                const lead = first.getDay();
                const total = daysIn(year, m);
                return (
                    <div key={m} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                        <button onClick={() => onPickMonth(m)} className="text-sm font-semibold text-white hover:text-orange-400 transition mb-2">{MONTHS[m]}</button>
                        <div className="grid grid-cols-7 gap-y-0.5 text-center">
                            {WEEKDAYS.map((w) => <span key={w} className="text-[9px] text-gray-600">{w[0]}</span>)}
                            {Array.from({ length: lead }, (_, i) => <span key={`b${i}`} />)}
                            {Array.from({ length: total }, (_, i) => {
                                const k = ymd(new Date(year, m, i + 1));
                                const has = (items.get(k) || []).length > 0;
                                return (
                                    <button
                                        key={k} onClick={() => onPickDay(k)}
                                        className={`text-[10px] h-5 rounded transition hover:bg-white/10 ${k === today ? "bg-orange-600 text-white font-bold" : has ? "bg-orange-500/20 text-orange-300" : "text-gray-400"}`}
                                    >{i + 1}</button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

/* ════════════════════════════════════════════════════════════════════
   POST HELPERS (display only)
   ════════════════════════════════════════════════════════════════════ */
// Unique media of a post (one entry per output — a video shared by two platforms shows once).
const mediaFor = (entry) => {
    const seen = new Map();
    (entry.platforms || []).forEach((id) => {
        const p = PLATFORM_BY_ID[id];
        const o = p && entry.outputs?.[p.outputKey];
        if (!o?.url) return;
        if (!seen.has(p.outputKey)) seen.set(p.outputKey, { key: p.outputKey, url: o.url, type: o.type || (p.type === "video" ? "video" : "image"), names: [] });
        seen.get(p.outputKey).names.push(p.name);
    });
    return [...seen.values()];
};

// Post now / Cancel / Delete — same store calls as before.
const PostActions = ({ it, now, store, labels = false }) => {
    const [busy, setBusy] = useState(false);
    const run = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
    const { entry, occ } = it;
    const st = occStatus(occ, now);
    const series = entry.occurrences.length;
    const base = "inline-flex items-center gap-1.5 rounded-lg border border-white/10 text-gray-300 transition " + (labels ? "px-3 py-2 text-xs font-medium" : "p-1.5");
    return (
        <div className="flex items-center gap-1.5 shrink-0" onDoubleClick={(e) => e.stopPropagation()}>
            {busy && <Loader2 size={14} className="animate-spin text-gray-500" />}
            {st === "scheduled" && (
                <>
                    <button disabled={busy} onClick={() => run(() => store.postNow(entry.id, occ.id))} title="Post right now" className={`${base} hover:text-orange-400 hover:border-orange-500/50`}><Zap size={14} />{labels && "Post now"}</button>
                    <button disabled={busy} onClick={() => run(() => store.cancelOccurrence(entry.id, occ.id))} title="Cancel this post" className={`${base} hover:text-yellow-400 hover:border-yellow-500/50`}><X size={14} />{labels && "Cancel"}</button>
                </>
            )}
            <button
                disabled={busy} title={series > 1 ? "Delete the whole series" : "Delete"}
                onClick={() => { if (window.confirm(series > 1 ? `Delete this whole series (${series} posts)? Pending posts will be cancelled.` : "Delete this scheduled post?")) run(() => store.removeEntry(entry.id)); }}
                className={`${base} hover:text-red-400 hover:border-red-500/50`}
            ><Trash2 size={14} />{labels && "Delete"}</button>
        </div>
    );
};

const CardMedia = ({ entry }) => {
    const m = mediaFor(entry)[0];
    const fallback = libThumb(entry.outputs);
    const url = m?.type === "video" ? null : (m?.url || fallback);
    const video = m?.type === "video" ? m.url : (!url && entry.outputs?.video?.url) || null;
    return (
        <div className="relative h-32 bg-black/50 flex items-center justify-center overflow-hidden">
            {url && <img src={url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
            {!url && video && <video src={`${video}#t=0.1`} muted playsInline preload="metadata" className="w-full h-full object-cover" />}
            {!url && !video && <Film size={28} className="text-gray-600" />}
            {video && !url && <span className="absolute bottom-2 right-2 text-[10px] px-1.5 py-0.5 rounded bg-black/70 text-red-300 border border-red-500/30">Video</span>}
        </div>
    );
};

/* ════════════════════════════════════════════════════════════════════
   AGENDA — slider card + list row + detail popup
   ════════════════════════════════════════════════════════════════════ */
const AgendaCard = ({ it, now, store, onOpen }) => {
    if (it.kind === "note") {
        return (
            <div onDoubleClick={onOpen} title="Double-click to view full note" className="snap-start shrink-0 w-[260px] sm:w-[300px] rounded-2xl border border-sky-500/20 bg-sky-500/5 hover:border-sky-500/40 transition p-4 flex flex-col gap-2 cursor-pointer select-none">
                <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-300"><StickyNote size={14} /> Note</span>
                    <button onClick={() => store.removeEntry(it.entry.id)} onDoubleClick={(e) => e.stopPropagation()} title="Delete note" className="text-gray-500 hover:text-red-400 transition"><Trash2 size={14} /></button>
                </div>
                <p className="text-sm text-white line-clamp-4 break-words whitespace-pre-wrap flex-1">{it.entry.text}</p>
                <div className="flex items-center justify-between">
                    <span className="text-[11px] text-gray-500">{it.entry.time ? fmtTime(it.entry.time) : "All day"}</span>
                    <button onClick={onOpen} className="inline-flex items-center gap-1 text-[11px] text-gray-300 hover:text-orange-400 transition"><Eye size={13} /> View</button>
                </div>
            </div>
        );
    }
    const { entry, occ } = it;
    const st = occStatus(occ, now);
    const series = entry.occurrences.length;
    return (
        <div onDoubleClick={onOpen} title="Double-click to view full post" className="snap-start shrink-0 w-[260px] sm:w-[300px] rounded-2xl border border-white/10 bg-white/[0.02] hover:border-orange-500/40 transition overflow-hidden flex flex-col cursor-pointer select-none">
            <div className="relative">
                <CardMedia entry={entry} />
                <span className="absolute top-2 left-2 text-xs font-semibold text-white bg-black/70 backdrop-blur px-2 py-1 rounded-lg">{fmtTime(it.time)}</span>
                <span className={`absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full border font-semibold bg-black/70 backdrop-blur ${STATUS_STYLE[st]}`}>{STATUS_LABEL[st]}</span>
            </div>
            <div className="p-3 flex-1 flex flex-col gap-2">
                <div className="flex flex-wrap gap-1">
                    {entry.platforms.map((id) => (
                        <span key={id} className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-300 border border-orange-500/20">{PLATFORM_BY_ID[id]?.name}</span>
                    ))}
                </div>
                <p className="text-xs text-gray-400 line-clamp-2 break-words min-h-[2rem]">{entry.caption || "No caption"}</p>
                {series > 1 && <span className="inline-flex items-center gap-1 text-[10px] text-gray-500"><Repeat size={10} />{entry.repeat} · {series} posts</span>}
            </div>
            <div className="px-3 pb-3 flex items-center justify-between gap-2">
                <button onClick={onOpen} className="inline-flex items-center gap-1.5 text-xs text-gray-300 hover:text-orange-400 transition"><Eye size={14} /> View</button>
                <PostActions it={it} now={now} store={store} />
            </div>
        </div>
    );
};

const AgendaRow = ({ it, now, store, onOpen }) => {
    if (it.kind === "note") {
        return (
            <div onDoubleClick={onOpen} className="flex items-start gap-3 rounded-xl border border-sky-500/20 bg-sky-500/5 p-3 cursor-pointer select-none">
                <StickyNote size={16} className="text-sky-300 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                    <p className="text-sm text-white whitespace-pre-wrap break-words line-clamp-3">{it.entry.text}</p>
                    <p className="text-[11px] text-gray-500 mt-1">
                        {it.entry.time ? `${fmtTime(it.entry.time)} · ` : ""}
                        {it.entry.endDate ? `${fmtShort(parseYmd(it.entry.date))} – ${fmtShort(parseYmd(it.entry.endDate))}` : "Note"}
                    </p>
                </div>
                <button onClick={onOpen} title="View" className="text-gray-500 hover:text-orange-400 transition"><Eye size={15} /></button>
                <button onClick={() => store.removeEntry(it.entry.id)} onDoubleClick={(e) => e.stopPropagation()} title="Delete note" className="text-gray-500 hover:text-red-400 transition"><Trash2 size={15} /></button>
            </div>
        );
    }
    const { entry, occ } = it;
    const st = occStatus(occ, now);
    const series = entry.occurrences.length;
    return (
        <div onDoubleClick={onOpen} title="Double-click to view full post" className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] hover:border-orange-500/30 transition p-3 cursor-pointer select-none">
            <Thumb outputs={entry.outputs} />
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-white">{fmtTime(it.time)}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${STATUS_STYLE[st]}`}>{STATUS_LABEL[st]}</span>
                    {series > 1 && <span className="inline-flex items-center gap-1 text-[10px] text-gray-500"><Repeat size={10} />{entry.repeat} · {series} posts</span>}
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                    {entry.platforms.map((id) => (
                        <span key={id} className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-300 border border-orange-500/20">{PLATFORM_BY_ID[id]?.name}</span>
                    ))}
                </div>
                {entry.caption && <p className="text-xs text-gray-400 mt-1.5 line-clamp-2 break-words">{entry.caption}</p>}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={onOpen} title="View full post" className="p-1.5 rounded-lg border border-white/10 text-gray-300 hover:text-orange-400 hover:border-orange-500/50 transition"><Eye size={14} /></button>
                <PostActions it={it} now={now} store={store} />
            </div>
        </div>
    );
};

// Horizontal slider with arrow buttons (scroll-snap, also swipeable on touch).
const AgendaSlider = ({ children, resetKey, count }) => {
    const ref = useRef(null);
    const [edge, setEdge] = useState({ l: false, r: false });
    const update = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        setEdge({ l: el.scrollLeft > 4, r: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
    }, []);
    useEffect(() => {
        ref.current?.scrollTo({ left: 0 });
        const id = requestAnimationFrame(update);
        window.addEventListener("resize", update);
        return () => { cancelAnimationFrame(id); window.removeEventListener("resize", update); };
    }, [resetKey, count, update]);
    const go = (dir) => ref.current?.scrollBy({ left: dir * ref.current.clientWidth * 0.8, behavior: "smooth" });
    const arrow = "absolute top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/80 border border-orange-500/40 text-orange-400 hover:bg-orange-600 hover:text-white flex items-center justify-center transition shadow-lg";
    return (
        <div className="relative">
            {edge.l && <button onClick={() => go(-1)} aria-label="Scroll left" className={`${arrow} left-0 -translate-x-2`}><ChevronLeft size={18} /></button>}
            <div ref={ref} onScroll={update} className={`flex gap-3 overflow-x-auto snap-x snap-mandatory pb-3 ${THEMED_SCROLLBAR}`}>{children}</div>
            {edge.r && <button onClick={() => go(1)} aria-label="Scroll right" className={`${arrow} right-0 translate-x-2`}><ChevronRight size={18} /></button>}
        </div>
    );
};

// Full details of one post / note (opens on double-click or the “View” button).
const PostDetailModal = ({ it, now, store, onClose }) => {
    if (it.kind === "note") {
        return (
            <Modal title="Note" subtitle={`${fmtLong(parseYmd(it.entry.date))}${it.entry.time ? ` · ${fmtTime(it.entry.time)}` : ""}`} onClose={onClose}
                footer={<button onClick={onClose} className="px-4 py-2 rounded-lg border border-white/10 text-sm text-gray-300 hover:text-white transition">Close</button>}>
                <p className="text-sm text-white whitespace-pre-wrap break-words">{it.entry.text}</p>
                {it.entry.endDate && <p className="text-xs text-gray-500">Until {fmtLong(parseYmd(it.entry.endDate))}</p>}
            </Modal>
        );
    }
    const { entry, occ } = it;
    const at = new Date(occ.at);
    const st = occStatus(occ, now);
    const media = mediaFor(entry);
    const tags = parseHashtags(entry.hashtags);
    const series = entry.occurrences.length;
    return (
        <Modal
            title="Scheduled post"
            subtitle={`${fmtLong(at)} · ${fmtTime(hm(at))}`}
            onClose={onClose}
            footer={
                <>
                    <PostActions it={it} now={now} store={store} labels />
                    <button onClick={onClose} className="px-4 py-2 rounded-lg border border-white/10 text-xs font-medium text-gray-300 hover:text-white transition">Close</button>
                </>
            }
        >
            <div className="flex flex-wrap items-center gap-2">
                <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${STATUS_STYLE[st]}`}>{STATUS_LABEL[st]}</span>
                {entry.platforms.map((id) => (
                    <span key={id} className="text-[11px] px-2 py-0.5 rounded bg-orange-500/10 text-orange-300 border border-orange-500/20">{PLATFORM_BY_ID[id]?.name}</span>
                ))}
            </div>

            {media.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-2">
                    {media.map((m) => (
                        <div key={m.key} className="rounded-xl border border-white/10 overflow-hidden bg-black">
                            {m.type === "video"
                                ? <video src={m.url} controls playsInline className="w-full max-h-72 bg-black" />
                                : <img src={m.url} alt="" className="w-full max-h-72 object-contain bg-black" />}
                            <p className="text-[11px] text-gray-400 px-3 py-1.5 border-t border-white/10">{m.names.join(" · ")}</p>
                        </div>
                    ))}
                </div>
            )}

            <div>
                <Label>Caption</Label>
                <p className="text-sm text-white whitespace-pre-wrap break-words">{entry.caption || <span className="text-gray-500">No caption</span>}</p>
            </div>
            {tags.length > 0 && (
                <div>
                    <Label>Hashtags</Label>
                    <div className="flex flex-wrap gap-1.5">
                        {tags.map((t) => <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-gray-300">#{t}</span>)}
                    </div>
                </div>
            )}

            {series > 1 && (
                <div>
                    <Label>Series · {entry.repeat} · {series} posts{entry.until ? ` · until ${fmtShort(parseYmd(entry.until))}` : ""}</Label>
                    <div className={`max-h-36 overflow-y-auto rounded-xl border border-white/10 divide-y divide-white/5 ${THEMED_SCROLLBAR}`}>
                        {[...entry.occurrences].sort((a, b) => new Date(a.at) - new Date(b.at)).map((o) => {
                            const d = new Date(o.at); const s = occStatus(o, now);
                            return (
                                <div key={o.id} className={`flex items-center justify-between px-3 py-1.5 text-xs ${o.id === occ.id ? "bg-orange-500/10 text-white" : "text-gray-400"}`}>
                                    <span>{fmtShort(d)} · {fmtTime(hm(d))}</span>
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_STYLE[s]}`}>{STATUS_LABEL[s]}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </Modal>
    );
};

/* ════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════════════════════ */
const CampaignCalendar = ({ store, scheduleRequest }) => {
    const [view, setView] = useState("month");
    const [cursor, setCursor] = useState(() => new Date());
    const [selected, setSelected] = useState(() => ymd(new Date()));
    const [modal, setModal] = useState(null); // { type: 'schedule'|'note', date, contentId? }
    const [notice, setNotice] = useState("");
    const [libOpen, setLibOpen] = useState(false);
    const [now, setNow] = useState(() => new Date());
    const [calOpen, setCalOpen] = useState(false);       // calendar panel (opened by the date button)
    const [layout, setLayout] = useState("slider");       // "slider" | "list"
    const [detailKey, setDetailKey] = useState(null);     // item opened in the detail popup

    useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);
    const today = ymd(now);

    // "Schedule this content" button from the AI Media Generator step opens the modal pre-filled.
    useEffect(() => {
        if (scheduleRequest?.contentId) setModal({ type: "schedule", date: ymd(new Date()), contentId: scheduleRequest.contentId });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scheduleRequest]);

    const items = useMemo(() => {
        const map = new Map();
        const push = (k, v) => { if (!map.has(k)) map.set(k, []); map.get(k).push(v); };
        store.entries.forEach((e) => {
            if (e.kind === "note") {
                const end = e.endDate ? parseYmd(e.endDate) : parseYmd(e.date);
                let d = parseYmd(e.date);
                for (let n = 0; d <= end && n < 400; n++, d = addDays(d, 1)) {
                    push(ymd(d), { key: `${e.id}:${ymd(d)}`, kind: "note", time: e.time || "", entry: e });
                }
            } else {
                (e.occurrences || []).forEach((o) => {
                    const at = new Date(o.at);
                    push(ymd(at), { key: o.id, kind: "post", time: hm(at), entry: e, occ: o });
                });
            }
        });
        map.forEach((list) => list.sort((a, b) => (a.time || "00:00").localeCompare(b.time || "00:00")));
        return map;
    }, [store.entries]);

    const itemByKey = useMemo(() => {
        const m = new Map();
        items.forEach((list) => list.forEach((it) => m.set(it.key, it)));
        return m;
    }, [items]);
    const detailItem = detailKey ? itemByKey.get(detailKey) : null;

    const upcoming = useMemo(() => {
        let n = 0;
        store.entries.forEach((e) => { if (e.kind !== "note") (e.occurrences || []).forEach((o) => { if (occStatus(o, now) === "scheduled") n++; }); });
        return n;
    }, [store.entries, now]);

    const agendaDate = view === "day" ? ymd(cursor) : selected;
    const agendaItems = items.get(agendaDate) || [];

    const shift = (dir) => setCursor((c) => {
        if (view === "month") return addMonthsClamped(c, dir);
        if (view === "year") return addMonthsClamped(c, 12 * dir);
        if (view === "week") return addDays(c, 7 * dir);
        return addDays(c, dir);
    });
    const selectDay = (k) => { setSelected(k); setCursor(parseYmd(k)); };
    const pickDay = (k) => { selectDay(k); setCalOpen(false); };
    const goToday = () => { const t = new Date(); setCursor(t); setSelected(ymd(t)); };

    const label =
        view === "month" ? cursor.toLocaleDateString("en-IN", { month: "long", year: "numeric" })
            : view === "year" ? String(cursor.getFullYear())
                : view === "week" ? `${fmtShort(startOfWeek(cursor))} – ${fmtShort(addDays(startOfWeek(cursor), 6))}, ${addDays(startOfWeek(cursor), 6).getFullYear()}`
                    : fmtLong(cursor);

    const afterSave = (firstDate, msg) => {
        setModal(null);
        setNotice(msg || "");
        selectDay(ymd(firstDate));
    };

    // Week strip (quick day picker under the date button)
    const stripStart = startOfWeek(parseYmd(agendaDate));
    const moveWeek = (dir) => selectDay(ymd(addDays(parseYmd(agendaDate), 7 * dir)));

    return (
        <div className="p-4 sm:p-6">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center shrink-0">
                        <CalendarDays size={20} className="text-orange-500" />
                    </div>
                    <div>
                        <h2 className="text-lg sm:text-xl font-bold text-white">Content Calendar</h2>
                        <p className="text-gray-400 text-xs sm:text-sm">Schedule saved content to auto-post on social media, and keep notes by day, week, month or year.</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setModal({ type: "note", date: agendaDate })} className="inline-flex items-center gap-2 border border-white/10 hover:border-orange-500/50 hover:text-orange-400 text-gray-300 px-3.5 py-2 rounded-lg text-sm font-medium transition">
                        <StickyNote size={15} /> Note
                    </button>
                    <button onClick={() => setModal({ type: "schedule", date: agendaDate })} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded-lg text-sm font-semibold transition">
                        <Plus size={16} /> Schedule Post
                    </button>
                </div>
            </div>

            {notice && (
                <div className="mt-4 flex items-start gap-2 text-xs text-orange-300 bg-orange-500/10 border border-orange-500/30 rounded-lg px-3 py-2.5">
                    <AlertCircle size={14} className="mt-0.5 shrink-0" />
                    <span className="flex-1">{notice}</span>
                    <button onClick={() => setNotice("")} aria-label="Dismiss"><X size={14} /></button>
                </div>
            )}

            {/* Date bar — click the date to open / close the calendar */}
            <div className="mt-5 flex flex-wrap items-center gap-2">
                <button
                    onClick={() => setCalOpen((o) => !o)} aria-expanded={calOpen}
                    className={`inline-flex items-center gap-2.5 rounded-xl border px-4 py-2.5 text-sm font-semibold transition ${calOpen ? "border-orange-500 bg-orange-500/10 text-orange-300" : "border-white/10 text-white hover:border-orange-500/50"}`}
                >
                    <CalendarDays size={16} className="text-orange-500" />
                    {fmtLong(parseYmd(agendaDate))}
                    <ChevronDown size={15} className={`text-orange-500 transition-transform ${calOpen ? "rotate-180" : ""}`} />
                </button>
                <button onClick={goToday} className="px-3 py-2.5 rounded-xl border border-white/10 text-xs font-medium text-gray-300 hover:text-orange-400 hover:border-orange-500/50 transition">Today</button>
                <span className="ml-auto text-xs text-gray-500">{upcoming} upcoming post{upcoming !== 1 ? "s" : ""}</span>
            </div>

            {/* Calendar panel */}
            {calOpen && (
                <div className="mt-3 rounded-2xl border border-orange-600/20 bg-white/[0.02] p-3 sm:p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <div className="flex items-center gap-2">
                            <button onClick={() => shift(-1)} aria-label="Previous" className="p-2 rounded-lg border border-white/10 text-gray-300 hover:text-orange-400 hover:border-orange-500/50 transition"><ChevronLeft size={16} /></button>
                            <button onClick={() => shift(1)} aria-label="Next" className="p-2 rounded-lg border border-white/10 text-gray-300 hover:text-orange-400 hover:border-orange-500/50 transition"><ChevronRight size={16} /></button>
                            <span className="ml-1 text-sm sm:text-base font-semibold text-white">{label}</span>
                        </div>
                        <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
                            {["day", "week", "month", "year"].map((v) => (
                                <button key={v} onClick={() => setView(v)} className={`px-3 py-2 text-xs font-medium capitalize transition ${view === v ? "bg-orange-600 text-white" : "text-gray-300 hover:bg-white/5"}`}>{v}</button>
                            ))}
                        </div>
                    </div>
                    {view === "month" && <MonthView cursor={cursor} selected={selected} items={items} today={today} now={now} onSelect={pickDay} />}
                    {view === "week" && <WeekView cursor={cursor} selected={selected} items={items} today={today} now={now} onSelect={pickDay} />}
                    {view === "year" && (
                        <YearView
                            cursor={cursor} items={items} today={today}
                            onPickMonth={(m) => { setCursor(new Date(cursor.getFullYear(), m, 1)); setView("month"); }}
                            onPickDay={(k) => { selectDay(k); setView("day"); setCalOpen(false); }}
                        />
                    )}
                    {view === "day" && <p className="text-sm text-gray-500 text-center py-6">Day view — use the arrows to move one day at a time. Pick Week or Month to choose a date.</p>}
                </div>
            )}

            {/* Week strip */}
            <div className="mt-3 flex items-center gap-1.5">
                <button onClick={() => moveWeek(-1)} aria-label="Previous week" className="p-1.5 rounded-lg text-gray-400 hover:text-orange-400 hover:bg-white/5 transition shrink-0"><ChevronLeft size={16} /></button>
                <div className="grid grid-cols-7 gap-1.5 flex-1">
                    {Array.from({ length: 7 }, (_, i) => addDays(stripStart, i)).map((d) => {
                        const k = ymd(d);
                        const n = (items.get(k) || []).length;
                        const sel = k === agendaDate;
                        return (
                            <button
                                key={k} onClick={() => selectDay(k)}
                                className={`relative rounded-xl border py-2 flex flex-col items-center gap-0.5 transition ${sel ? "bg-gradient-to-b from-orange-300/25 via-orange-500/15 to-orange-600/5 backdrop-blur-md border-orange-300/50 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.3),0_0_22px_rgba(249,115,22,0.25)]" : k === today ? "border-orange-500/50 text-orange-300 hover:bg-white/5" : "border-white/10 text-gray-300 hover:bg-white/5"}`}
                            >
                                <span className={`text-[10px] uppercase tracking-wide ${sel ? "text-orange-200" : "text-gray-500"}`}>{WEEKDAYS[d.getDay()]}</span>
                                <span className="text-sm font-bold">{d.getDate()}</span>
                                <span className={`h-1.5 w-1.5 rounded-full ${n ? (sel ? "bg-orange-300" : "bg-orange-500") : "bg-transparent"}`} />
                            </button>
                        );
                    })}
                </div>
                <button onClick={() => moveWeek(1)} aria-label="Next week" className="p-1.5 rounded-lg text-gray-400 hover:text-orange-400 hover:bg-white/5 transition shrink-0"><ChevronRight size={16} /></button>
            </div>

            {/* Agenda for the chosen day */}
            <div className="mt-6">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="flex items-baseline gap-2">
                        <h3 className="text-sm font-semibold text-white">{fmtLong(parseYmd(agendaDate))}</h3>
                        <span className="text-xs text-gray-500">{agendaItems.length} item{agendaItems.length !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
                        <button onClick={() => setLayout("slider")} title="Slider" className={`px-2.5 py-1.5 transition ${layout === "slider" ? "bg-orange-600 text-white" : "text-gray-400 hover:bg-white/5"}`}><LayoutGrid size={14} /></button>
                        <button onClick={() => setLayout("list")} title="List" className={`px-2.5 py-1.5 transition ${layout === "list" ? "bg-orange-600 text-white" : "text-gray-400 hover:bg-white/5"}`}><List size={14} /></button>
                    </div>
                </div>
                {agendaItems.length === 0 ? (
                    <p className="text-sm text-gray-500 border border-dashed border-white/10 rounded-xl px-4 py-6 text-center">
                        Nothing planned for this day. Use “Schedule Post” or “Note” to add something.
                    </p>
                ) : layout === "slider" ? (
                    <>
                        <AgendaSlider resetKey={agendaDate} count={agendaItems.length}>
                            {agendaItems.map((it) => <AgendaCard key={it.key} it={it} now={now} store={store} onOpen={() => setDetailKey(it.key)} />)}
                        </AgendaSlider>
                        <p className="text-[11px] text-gray-600">Double-click a card (or tap View) to see the full post.</p>
                    </>
                ) : (
                    <div className={`space-y-2.5 max-h-[420px] overflow-y-auto pr-1 ${THEMED_SCROLLBAR}`}>
                        {agendaItems.map((it) => <AgendaRow key={it.key} it={it} now={now} store={store} onOpen={() => setDetailKey(it.key)} />)}
                    </div>
                )}
            </div>

            {/* Saved content */}
            <div className="mt-6 border-t border-white/5 pt-4">
                <button onClick={() => setLibOpen((o) => !o)} className="w-full flex items-center justify-between text-left" aria-expanded={libOpen}>
                    <span className="flex items-center gap-2 text-sm font-semibold text-white">
                        <ImageIcon size={16} className="text-orange-500" /> Saved content
                        <span className="text-xs font-normal text-gray-500">({store.library.length})</span>
                    </span>
                    <ChevronDown size={16} className={`text-orange-500 transition-transform ${libOpen ? "rotate-180" : ""}`} />
                </button>
                {libOpen && (
                    store.library.length === 0 ? (
                        <p className="text-sm text-gray-500 mt-3">Nothing saved yet. Content you generate in the AI Media Generator above is saved here automatically, ready to schedule.</p>
                    ) : (
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {store.library.map((c) => (
                                <div key={c.id} className="flex items-center gap-3 rounded-xl border border-white/10 p-3">
                                    <Thumb outputs={c.outputs} size={52} />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-[11px] text-gray-500 mb-1">{new Date(c.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</p>
                                        <OutputBadges outputs={c.outputs} />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <button onClick={() => setModal({ type: "schedule", date: agendaDate, contentId: c.id })} className="text-[11px] px-2.5 py-1 rounded-lg bg-orange-600 hover:bg-orange-500 text-white font-semibold transition">Schedule</button>
                                        <button onClick={() => { if (window.confirm("Remove this from saved content? Already scheduled posts are not affected.")) store.removeFromLibrary(c.id); }} className="text-[11px] px-2.5 py-1 rounded-lg border border-white/10 text-gray-400 hover:text-red-400 hover:border-red-500/50 transition">Remove</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                )}
            </div>

            {detailItem && <PostDetailModal it={detailItem} now={now} store={store} onClose={() => setDetailKey(null)} />}
            {modal?.type === "schedule" && (
                <ScheduleModal store={store} initialDate={modal.date} initialContentId={modal.contentId} onClose={() => setModal(null)} onDone={afterSave} />
            )}
            {modal?.type === "note" && (
                <NoteModal store={store} initialDate={modal.date} onClose={() => setModal(null)} onDone={afterSave} />
            )}
        </div>
    );
};

export default CampaignCalendar;